import React, { FC, useState, useEffect, FormEvent } from 'react';
import { Button, Checkbox, Form, Grid, Segment, toast } from '../../kit';
import { ApiProfile } from '../../api/auth';
import { useProfile, reloadProfile } from '../../lib/auth';
import { regions } from '../ProfileView';
import { useDropdownOptions } from '../../lib/hooks';
import styles from './styles.module.scss';
import Avatar from '../../components/Avatar';
import ChangeHandler from '../../modules/ChangeHandler';
import api from '../../api';

const AccountView: FC = (): JSX.Element | null => {
    const [ avatar, setAvatar ] = useState<string>();
    const [ changesMade, setChangesMade ] = useState<boolean>(false);
    const [ confirmPassword, setConfirmPassword ] = useState<string>('');
    const [ errors, setErrors ] = useState<any>({});
    const [ file, setFile ] = useState<File>();
    const [ newPassword, setNewPassword ] = useState<string>('');
    const [ password, setPassword ] = useState<string>('');
    const [ profile, setProfile ] = useState<ApiProfile>();
    const auth = useProfile();
    // const expertiseOptions = useDropdownOptions('labels?type=expertise');
    // const functionOptions = useDropdownOptions('labels?type=function');

    useEffect(() => {
        if (!auth) return;
        setProfile({
            ...auth,
            introduction: (auth.introduction || '').replace(/<br \/>/g, ''),
        });
        setAvatar(auth?.avatar);
        setTimeout(() => (window as any).AOS.init(), 200);
    }, [auth]);

    const handleAvatar = (file: File): void => {
        const reader = new FileReader();
        reader.onload = () => {
            setFile(file);
            setAvatar(reader.result as string);
            setChangesMade(true);
        };
        reader.readAsDataURL(file);
    }

    const handleInput = ({ name, value }: any): void => {
        if (!profile) return;
        setProfile({
            ...profile,
            [name]: value,
        });
        setChangesMade(true);
    }

    const handleReset = () => {
        reloadProfile();
        setChangesMade(false);
        setErrors({});
    };

    const save = (e?: FormEvent): void => {
        e?.preventDefault();
        if (!profile) return;

        api.updateProfile(profile).then(({ data }) => {
            if (file) {
                api.uploadAvatar(file).then(() => {
                    setFile(undefined);
                    setAvatar(data.avatar);
                    reloadProfile();
                });
            } else {
                reloadProfile();
            }

            toast('Gegevens opgeslagen');
            setChangesMade(false);
        }).catch((err) => {
            setErrors(err.response.data.errors);
        });
    }

    const savePassword = (): void => {
        if (password === '' || newPassword === '') return;
        setErrors({});

        if (newPassword !== confirmPassword) {
            setErrors({ confirmPassword: 'Wachtwoorden komen niet overeen' });
        } else {
            api.savePassword(password, newPassword).then(() => {
                setPassword('');
                setNewPassword('');
                setConfirmPassword('');
                toast('Wachtwoord succesvol aangepast', 'success');
            }).catch((err) => {
                setErrors(err.response.data.errors);
            });
        }
    }

    return profile ? (<>
        <ChangeHandler
            changesMade={changesMade}
            onCancel={handleReset}
            onSave={save}
        />
        <Form
            className="container"
            onSubmit={(e: FormEvent) => save(e)}
            style={{ maxWidth: 1140 }}
        >
            <h2 className="section-title" data-aos="fade-up">Mijn account</h2>
            <Grid.Row>
                <Grid.Column lg={7} data-aos="fade-up" data-aos-delay="50">
                    <Segment header="Profielgegevens">
                        <Grid.Row style={{ marginBottom: 16 }}>
                            <Grid.Column lg={4}>
                                <Form.Input
                                    error={errors.firstName}
                                    label="Voornaam"
                                    name="first_name"
                                    onChange={handleInput}
                                    required
                                    value={profile.first_name}
                                />
                            </Grid.Column>
                            <Grid.Column lg={3}>
                                <Form.Input
                                    label="Tussenvoegsel"
                                    name="affix"
                                    onChange={handleInput}
                                    value={profile.affix}
                                />
                            </Grid.Column>
                            <Grid.Column lg={5}>
                                <Form.Input
                                    error={errors.lastName}
                                    label="Achternaam"
                                    name="last_name"
                                    onChange={handleInput}
                                    required
                                    value={profile.last_name}
                                />
                            </Grid.Column>
                        </Grid.Row>
                        <Form.Input
                            error={errors.emailAddress}
                            label="E-mailadres"
                            name="email_address"
                            onChange={handleInput}
                            required
                            type="email"
                            value={profile.email_address}
                        />
                        <Form.Input
                            label="Organisatie"
                            name="organisation"
                            onChange={handleInput}
                            value={profile.organisation}
                        />
                        <Form.Input
                            label="Functie"
                            name="function"
                            onChange={handleInput}
                            // options={functionOptions.map((o) => ({
                            //     text: o.text,
                            //     value: o.text,
                            // }))}
                            value={profile.function}
                        />
                        <Form.Input
                            label="Expertise"
                            name="expertise"
                            onChange={handleInput}
                            // options={expertiseOptions.map((o) => ({
                            //     text: o.text,
                            //     value: o.text,
                            // }))}
                            value={profile.expertise}
                        />
                        <Form.Dropdown
                            label="Werkgebied (geografisch)"
                            multiple
                            name="work_area"
                            onChange={handleInput}
                            options={regions.map((o) => ({
                                text: o,
                                value: o,
                            }))}
                            value={profile.work_area}
                        />
                        <Form.Input
                            label="Woonplaats"
                            name="city"
                            onChange={handleInput}
                            value={profile.city}
                        />
                        <Form.Textarea
                            autoGrow
                            label="Introductie"
                            name="introduction"
                            onChange={handleInput}
                            value={profile.introduction}
                        />
                    </Segment>
                </Grid.Column>
                <Grid.Column lg={5} data-aos="fade-up" data-aos-delay="100">
                    <Segment header="Profielafbeelding">
                        <div className={styles.avatarContainer}>
                            <Avatar profile={{ ...profile, avatar } as ApiProfile} />
                            <div style={{ flex: 1, marginLeft: '1rem' }}>
                                <Form.Input
                                    accept="image/*"
                                    onChange={({ files }) => handleAvatar(files[0])}
                                    type="file"
                                />
                            </div>
                        </div>
                    </Segment>
                    <h2>Instellingen</h2>
                    <Segment className={styles.setting}>
                        <Checkbox
                            checked={profile.show_profile}
                            name="show_profile"
                            onChange={handleInput}
                            label="Toon mijn profiel"
                            toggle
                        />
                    </Segment>
                    <Segment className={styles.setting}>
                        <Checkbox
                            checked={profile.is_available}
                            name="is_available"
                            onChange={handleInput}
                            label="Beschikbaar voor opdrachten"
                            toggle
                        />
                    </Segment>
                    <Segment className={styles.setting}>
                        <Checkbox
                            checked={profile.allow_dm}
                            name="allow_dm"
                            onChange={handleInput}
                            label="Anderen mogen mij privéberichten sturen"
                            toggle
                        />
                    </Segment>
                    <Segment className={styles.setting}>
                        <Checkbox
                            checked={profile.send_email_dm}
                            name="send_email_dm"
                            onChange={handleInput}
                            label="Stuur e-mail notificatie wanneer iemand mij een privébericht stuurt"
                            toggle
                        />
                    </Segment>
                    <Segment className={styles.setting}>
                        <Checkbox
                            checked={profile.send_email_forumpost}
                            name="send_email_forumpost"
                            onChange={handleInput}
                            label="Stuur e-mail notificatie wanneer iemand een reactie plaatst op een forumbericht wat ik volg"
                            toggle
                        />
                    </Segment>
                    <Segment header="Beveiliging">
                        <div style={{ marginBottom: 8 }}>
                            <Form.Input
                                error={errors.password}
                                placeholder="Huidig wachtwoord"
                                onChange={({ value }) => setPassword(value)}
                                value={password}
                                type="password"
                            />
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <Form.Input
                                error={errors.new_password}
                                placeholder="Nieuw wachtwoord"
                                onChange={({ value }) => setNewPassword(value)}
                                value={newPassword}
                                type="password"
                            />
                        </div>
                        <Form.Input
                            error={errors.confirmPassword}
                            placeholder="Bevestig nieuw wachtwoord"
                            onChange={({ value }) => setConfirmPassword(value)}
                            value={confirmPassword}
                            type="password"
                        />
                        {password !== '' && (
                            <Button
                                color="primary"
                                label="Opslaan"
                                onClick={() => savePassword()}
                            />
                        )}
                    </Segment>
                </Grid.Column>
            </Grid.Row>
        </Form>
    </>) : null;
}

export default AccountView;
